import { PrismicNextImage } from '@prismicio/next';
import { asText, Content, isFilled } from '@prismicio/client';

import {
  Box,
  Card,
  Heading,
  AutoGrid,
  CardBody,
  CardHero,
  CardContent,
} from '@preventx/oxygen';
import { RichText } from '@preventx/prismic-services';

// Services
import { applicationEnv } from '@/services/env/shared';

// Components
import { Link } from '@/components/Actions/Link';
import { VimeoContainer } from '@/components/Display/VimeoContainer';

export type WithGuidesSupportProps = {
  id: string;
  slice: Content.RegionComplexSliceWithGuidesAndSupport;
};

export const WithGuidesSupport = ({ id, slice }: WithGuidesSupportProps) => {
  const { primary } = slice;

  const { WebsiteUrl } = applicationEnv();

  const { title, guides } = primary;

  const layerOneItems: Content.RegionComplexSliceWithGuidesAndSupportPrimaryGuidesItem[] =
    [];

  const layerTwoItems: Content.RegionComplexSliceWithGuidesAndSupportPrimaryGuidesItem[] =
    [];

  // Purpose  : Splits into 2 separate arrays so we can render the content differently
  //            depending on how many items there are in the `guides` property
  for (let index = 0; index < guides.length; index++) {
    const element = guides[index];

    if (guides.length <= 3) {
      layerOneItems.push(element);

      continue;
    }

    index < 2 ? layerOneItems.push(element) : layerTwoItems.push(element);
  }

  return (
    <Box component="div" addFlow>
      <Heading component="h2" className="t-on-accent">
        {asText(title)}
      </Heading>

      <AutoGrid layout="thirds" component="div">
        {layerOneItems.map((guide, index) => {
          const { type, image, content, title_link, video_id } = guide;

          return (
            <Card
              variant="simple"
              component="article"
              key={`with-guides-support-layer-one-${id}-${index + 1}`}
            >
              {type === 'Vimeo' && (
                <VimeoContainer
                  figureAs="card-hero"
                  vimeoId={video_id ?? undefined}
                />
              )}

              {type === 'Image' && (
                <CardHero component="figure" ratio="widescreen">
                  <PrismicNextImage field={image} />
                </CardHero>
              )}

              <CardBody>
                <CardContent addFlow>
                  <Heading component="h3">
                    {isFilled.link(title_link) && (
                      <Link
                        type="link"
                        as="default"
                        href={title_link}
                        retainSearchParams
                      >
                        {title_link.text}
                      </Link>
                    )}

                    {!isFilled.link(title_link) && title_link.text}
                  </Heading>

                  <RichText
                    content={content}
                    variant="default"
                    websiteUrl={WebsiteUrl}
                  />
                </CardContent>
              </CardBody>
            </Card>
          );
        })}

        {layerTwoItems.length > 0 && (
          <Box component="div" addFlow>
            {layerTwoItems.map((guide, index) => {
              const { content, title_link } = guide;

              return (
                <Card
                  variant="simple"
                  component="article"
                  key={`with-guides-support-layer-two-${id}-${index + 1}`}
                >
                  <CardBody>
                    <CardContent addFlow>
                      <Heading component="h3">
                        {isFilled.link(title_link) && (
                          <Link
                            type="link"
                            as="default"
                            href={title_link}
                            retainSearchParams
                          >
                            {title_link.text}
                          </Link>
                        )}

                        {!isFilled.link(title_link) && title_link.text}
                      </Heading>

                      <RichText
                        content={content}
                        variant="default"
                        websiteUrl={WebsiteUrl}
                      />
                    </CardContent>
                  </CardBody>
                </Card>
              );
            })}
          </Box>
        )}
      </AutoGrid>
    </Box>
  );
};
